import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'lib-form-reset-action',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="btn-cursor subheading-primary frm-reset-btn"
      [class.subheading-default]="_isLoading()"
      (click)="handleClick()">
      @if (!confirmPending()) {
        reset form
      } @else {
        click again to confirm reset ({{ countdown() }}s)
      }
    </span>
  `,
})
export class FormResetActionComponent {
  loading = input<Observable<boolean>>();
  resetForm = output<void>();

  confirmPending = signal(false);
  countdown = signal(5);

  protected _isLoading = toSignal(
    toObservable(this.loading).pipe(
      switchMap(obs => obs ?? of(false)),
    ),
    { initialValue: false },
  );

  private readonly _destroyRef = inject(DestroyRef);
  private _intervalId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this._destroyRef.onDestroy(() => this._clearCountdown());
  }

  handleClick(): void {
    if (this._isLoading()) return;

    if (!this.confirmPending()) {
      this.confirmPending.set(true);
      this.countdown.set(5);
      this._startCountdown();
    } else {
      this._clearCountdown();
      this.resetForm.emit();
      this.confirmPending.set(false);
      this.countdown.set(5);
    }
  }

  private _startCountdown(): void {
    this._clearCountdown();
    this._intervalId = setInterval(() => {
      const current = this.countdown();
      if (current <= 1) {
        this._clearCountdown();
        this.confirmPending.set(false);
        this.countdown.set(5);
      } else {
        this.countdown.set(current - 1);
      }
    }, 1000);
  }

  private _clearCountdown(): void {
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }
}
